import { assessmentParse } from "@p0security/shared/assessment/dsl";
import { dslParse as dslParser } from "@p0security/shared/graph/dsl/index";
import { TargetNodeType } from "@p0security/shared/types/assessment/data/index";
import { ModalProps } from "antd";

import { MonitorWithMetadata } from "../../contexts/FindingsContext";

// Base props common to all modal modes
export type BaseMonitorModalProps = {
  open: boolean;
  onCancel: () => void;
} & Omit<ModalProps, "onCancel" | "open">;

// Discriminated union for different modal modes
export type CreateMonitorModalProps = BaseMonitorModalProps & {
  mode: "create-monitor";
  initialSearchTerm: string;
  show: TargetNodeType;
  templateValues?: Partial<MonitorWithMetadata>;
  templateKey?: string;
  /** Whether to allow selecting a preset template when creating */
  allowTemplateSelection?: boolean;
  /** If true, auto-enable create monitor and focus template select */
  startWithTemplate?: boolean;
};

export type EditMonitorModalProps = BaseMonitorModalProps & {
  mode: "edit-monitor";
  monitor: MonitorWithMetadata | undefined;
  isCustom: boolean;
};

export type SaveSearchBaseProps = {
  initialSearchTerm: string;
  show: TargetNodeType;
  /** When provided, saving the search will invoke this callback */
  onSaveSearch: (name: string, searchTerm: string, show: string) => void;
  /** Whether to allow selecting a preset template when creating */
  allowTemplateSelection?: boolean;
  /** If true, auto-enable create monitor and focus template select */
  startWithTemplate?: boolean;
};

export type SaveSearchFormProps = SaveSearchBaseProps & {
  onCancel: () => void;
};

export type SaveSearchModalProps = BaseMonitorModalProps &
  SaveSearchBaseProps & {
    mode: "save-search";
  };

export type MonitorModalProps =
  | CreateMonitorModalProps
  | EditMonitorModalProps
  | SaveSearchModalProps;

export interface SaveSearchFormValues {
  label: string;
  where: string;
}

/**
 * Validates a search term using the assessment parser.
 * Returns null if valid, or an error message if invalid.
 */
export const validateSearchTerm = (searchTerm: string): string | null => {
  if (searchTerm.trim().length === 0) {
    return null;
  }

  try {
    const result = assessmentParse(searchTerm)(dslParser);
    if ("warning" in result) {
      return result.warning;
    }
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : "Invalid query syntax.";
  }
};
